/**
 * Forgot password. Better Auth's reset is link-based: we ask for the address,
 * the API mails a one-hour link to /reset-password?token=…, and that page sets
 * the new password. So this screen has exactly two states — ask, and "check
 * your email" — with a resend that reuses the same request.
 */
import { useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronLeft } from 'lucide-react'
import { z } from 'zod'
import GenieLogo from '@/components/ui/icon/icons/GenieLogo'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { requestPasswordReset } from '@/lib/auth-client'
import { safeAuthRedirect } from '@/lib/auth-redirect'

const EmailOnlySchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})
type EmailFormData = z.infer<typeof EmailOnlySchema>

/** Where the emailed link lands: this app's own reset page. */
const resetPageUrl = (redirect: string) => `${window.location.origin}/reset-password?redirect=${encodeURIComponent(redirect)}`

export function PasswordRecovery() {
  const search = useSearch({ strict: false }) as { redirect?: string }
  const redirect = safeAuthRedirect(search.redirect)
  const [sentTo, setSentTo] = useState('')
  const [error, setError] = useState('')
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EmailFormData>({ resolver: zodResolver(EmailOnlySchema) })

  const request = async (email: string) => {
    const result = await requestPasswordReset({ email: email.trim(), redirectTo: resetPageUrl(redirect) })
    if (result.error) throw new Error(result.error.message || 'Could not send the reset email')
  }

  const submitEmail = async ({ email }: EmailFormData) => {
    setError('')
    try {
      await request(email)
      setSentTo(email)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unexpected error occurred')
    }
  }

  const resend = async () => {
    setResending(true)
    setResent(false)
    setError('')
    try {
      await request(sentTo)
      setResent(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unexpected error occurred')
    } finally {
      setResending(false)
    }
  }

  const busy = isSubmitting || resending

  return (
    <div className="md:bg-muted flex items-center justify-center bg-cover md:min-h-screen">
      <div className="flex h-screen w-full flex-col md:h-auto md:max-w-[440px] md:px-4 md:py-6">
        <Card className="rounded-none p-6 md:rounded-xl">
          <CardHeader className="mb-6 flex flex-col items-start border-b-0 p-0 text-left">
            <Link to="/login" search={{ redirect }} className={busy ? 'pointer-events-none opacity-50' : ''}>
              <GenieLogo className="mb-6 h-12 w-12" />
            </Link>
            {sentTo ? (
              <>
                <CardTitle className="text-lg font-bold">Check your email</CardTitle>
                <CardDescription>
                  If an account exists for <span className="text-foreground">{sentTo}</span>, you’ll receive a reset link. It works for one hour.
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-lg font-bold">Forgot your password?</CardTitle>
                <CardDescription>Enter your email and we will send you a link to set a new one.</CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent className="p-0">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {sentTo ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Nothing there? Look in spam, or send it again.
                </p>
                <Button size="lg" variant="outline" className="w-full" onClick={() => void resend()} disabled={busy}>
                  {resending ? 'Sending…' : resent ? 'Sent again' : 'Send again'}
                </Button>
                <Button size="lg" variant="ghost" className="w-full" disabled={busy} onClick={() => {
                  setSentTo('')
                  setError('')
                  setResent(false)
                }}>
                  Use a different email
                </Button>
                <Button asChild size="lg" variant="ghost" className="w-full">
                  <Link to="/login" search={{ redirect }}>
                    <ChevronLeft className="mr-1 h-4 w-4" /> Back to sign in
                  </Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(submitEmail)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="inline-block">Email</Label>
                  <Input id="email" type="email" placeholder="Email" autoComplete="email" autoFocus disabled={busy} {...register('email')} />
                  {errors.email && <p className="mt-1 text-sm text-destructive">{errors.email.message}</p>}
                </div>
                <Button size="lg" className="w-full" type="submit" disabled={busy}>
                  {isSubmitting ? 'Sending…' : 'Send reset link'}
                </Button>
                <Button asChild size="lg" variant="ghost" className="w-full">
                  <Link to="/login" search={{ redirect }}>
                    <ChevronLeft className="mr-1 h-4 w-4" /> Back to sign in
                  </Link>
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
